import React, { useRef, useState, useEffect } from "react";
import { FaPlayCircle } from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import Style from "../../../styles/features/treinos/ExibicaoTreinoDieta.module.css";
import { parseYouTubeId, youTubeEmbedUrl, youTubeThumbUrl } from "../utils/youtube.js";

export const VideoThumb = ({ url, thumbnail, onClick, width = 80, height = 56 }) => {
  const vref = useRef(null);
  const rootRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [nearViewport, setNearViewport] = useState(false);

  // Link do YouTube não gera frame em <video>; usa o thumbnail do próprio YouTube.
  const ytId = parseYouTubeId(url);
  const effectiveThumb = thumbnail || (ytId ? youTubeThumbUrl(ytId) : null);

  useEffect(() => { setReady(false); }, [url]);

  useEffect(() => {
    if (effectiveThumb || !url) return undefined;
    const element = rootRef.current;
    if (!element || typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setNearViewport(true),
      { rootMargin: "180px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [effectiveThumb, url]);

  const handleLoadedMetadata = () => {
    const v = vref.current;
    if (!v) return;
    try { v.currentTime = 0.1; } catch {}
  };

  const handleSeeked = () => setReady(true);

  return (
    <div
      ref={rootRef}
      role="button"
      onClick={onClick}
      style={{
        width,
        height,
        borderRadius: 12,
        overflow: "hidden",
        position: "relative",
        display: "grid",
        placeItems: "center",
        opacity: url ? 1 : 0.35,
      }}
      title={url ? "Ver vídeo" : "Sem vídeo"}
    >
      {effectiveThumb ? (
        <>
          <img
            src={effectiveThumb}
            alt=""
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              background: "rgba(0,0,0,0.15)",
            }}
          >
            <FaPlayCircle size={26} style={{ color: "rgba(255, 255, 255, 0.55)" }} />
          </div>
        </>
      ) : url && nearViewport ? (
        <>
          <video
            ref={vref}
            src={url}
            poster={thumbnail || undefined}
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
            onSeeked={handleSeeked}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: ready ? "none" : "blur(6px)",
              transform: "scale(1.05)",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              background: "rgba(0,0,0,0.15)",
            }}
          >
            <FaPlayCircle size={26} style={{ color: "rgba(255, 255, 255, 0.30)" }} />
          </div>
        </>
      ) : (
        <FaPlayCircle size={28} />
      )}
    </div>
  );
};

export const VideoModal = ({ show, onHide, url, url2, nome }) => {
  const [videoTab, setVideoTab] = useState(0);

  useEffect(() => { setVideoTab(0); }, [url, url2]);

  return (
    <Modal className={Style.modalVideo} show={show} onHide={onHide} centered size="lg">
      <Modal.Header className={Style.modalVideoHeader} closeButton>
        <Modal.Title>{nome || "Vídeo do exercício"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {url2 ? (
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {["Execução 1", "Execução 2"].map((label, i) => (
              <button
                key={i}
                onClick={() => setVideoTab(i)}
                style={{
                  padding: "6px 16px",
                  borderRadius: 99,
                  border: "none",
                  fontSize: 13,
                  fontWeight: videoTab === i ? 600 : 400,
                  background: videoTab === i ? "var(--color-accent)" : "#E9ECEF",
                  color: videoTab === i ? "var(--color-primary)" : "#495057",
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
        <div style={{ width: "100%" }}>
          {(() => {
            const currentUrl = videoTab === 0 ? url : url2;
            const ytId = parseYouTubeId(currentUrl);
            if (ytId) {
              // Link do YouTube: toca dentro do app via embed (sem sair do app).
              return (
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    paddingTop: "56.25%",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "#000",
                  }}
                >
                  <iframe
                    key={currentUrl}
                    src={youTubeEmbedUrl(ytId)}
                    title={nome || "Vídeo do exercício"}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      border: 0,
                    }}
                  />
                </div>
              );
            }
            return (
              <video
                key={currentUrl}
                controls
                autoPlay
                playsInline
                style={{ width: "100%", borderRadius: 12 }}
              >
                <source src={currentUrl} />
                Seu navegador não suporta vídeo.
              </video>
            );
          })()}
        </div>
      </Modal.Body>
      <Modal.Footer className={Style.modalVideoFooter}>
        <Button className={Style.modalVideobtn} variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
